/* ==========================================================================
   roam-3d-analytics.js — Ikarus 3D viewer engagement tracking
   --------------------------------------------------------------------------
   Pushes `roam_3d_*` events onto window.dataLayer. GTM (GTM-KJMM6QVG) picks
   them up with a single Custom Event trigger matching ^roam_3d_ and forwards
   them to GA4 (G-D0P34TDRPL) via one GA4 Event tag.

   Loaded (deferred) from BOTH Ikarus integrations:
     A. snippets/customizer-3d-preview.liquid  — in-customizer preview (luggage-b4u-v2)
     B. snippets/product-3d-viewer.liquid      — "View in 3D" modal (DC / default PDPs)
   The two are mutually exclusive by template, but the window-level guard below
   is mandatory anyway: main-product--default renders the 3D button 3x, and any
   shared theme JS pulled in from more than one place must be idempotent or the
   second evaluation re-binds and cancels the first's handlers.

   DESIGN NOTES / TRAPS THIS FILE EXISTS TO HANDLE
   -----------------------------------------------
   * The Ikarus viewer is a CROSS-ORIGIN iframe. We cannot observe rotate, zoom,
     pinch, AR, or clicks on its own controls. Everything here is either (a) a
     parent-side fact we own, or (b) explicitly-labelled inference.
   * Recolors are observable because WE send IKARUS_CONFIG — but sendConfig() is
     called on viewer-ready, on modal open, on every variant change, and (in the
     customizer) on EVERY history.pushState/replaceState from anywhere on the
     page. Most of those calls are not a user recolor. recolor() therefore
     (1) ignores empty configs, (2) treats the first non-empty config as a
     silent baseline, (3) requires a prior real user input on the page, and
     (4) debounces + diffs so a trim cascade (trim→wheels+binding+handles+zipper)
     is one event, not five.
   * Keep the runtime footprint near zero. These pages already sit near the iOS
     Safari per-process memory ceiling with a WebGL iframe open — no polling, no
     MutationObserver, passive listeners only, one 30s watchdog timer.

   This file NEVER throws into the host page: every public method is wrapped.
   If anything here fails, the 3D viewer keeps working and we just lose events.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__roam3dAnalyticsLoaded) return;
  window.__roam3dAnalyticsLoaded = true;

  var dl = (window.dataLayer = window.dataLayer || []);

  /* ── Config ────────────────────────────────────────────────────────────── */
  var RECOLOR_DEBOUNCE_MS = 350;
  /* First model load is documented at 15-35s (heaviest = large-check-in,
     da3d9a2c) and is slower again on a poor mobile connection. A 30s watchdog
     produced false ready_timeout errors in testing — keep this comfortably
     beyond the real worst case so the event means "never loaded", not "slow". */
  var READY_TIMEOUT_MS = 60000;
  var MAX_PARAM_LEN = 100;      // GA4 event-parameter value limit

  /* UUID prefix → case size. The Ikarus model UUID is per-size by definition,
     so this one map covers BOTH the b4u set and the Designer Collection set
     (which use different UUIDs for the same size). Keyed on the first 8 chars. */
  var SIZE_BY_UUID = {
    '234ca9d6': 'carry-on',
    '739c0169': 'carry-on-expandable',
    '8b5a9562': 'carry-on-expandable',
    'e09bc182': 'front-pocket-carry-on',
    '08374ce1': 'front-pocket-carry-on-expandable',
    'dd124d93': 'large-carry-on',
    '79d290bf': 'large-carry-on-expandable',
    '0b70d8c8': 'check-in',
    '70a0be7c': 'check-in-expandable',
    'da3d9a2c': 'large-check-in',
    '6fbf57a5': 'large-check-in-expandable',
    'cc1ea4bc': 'carry-on' // hardcoded fallback id in customizer-3d-preview
  };

  /* ── State ─────────────────────────────────────────────────────────────── */
  var s = {
    surface: '',          // 'customizer' | 'modal'
    productHandle: '',
    modelUuid: '',
    variantId: '',
    bootAt: now(),
    srcSetAt: 0,
    openedAt: 0,
    readyFired: false,
    viewFired: false,
    didInteract: false,
    userInput: false,     // has the shopper interacted with the PAGE at all
    lastConfig: null,
    lastHash: null,
    pendingFrom: null,
    recolorTimer: null,
    recolorCount: 0,
    watchdog: null,
    iframe: null
  };

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  /* ── dataLayer push ────────────────────────────────────────────────────── */
  function track(name, params) {
    try {
      var payload = { event: name };
      if (params) {
        for (var k in params) {
          if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
          var v = params[k];
          if (v === undefined || v === null || v === '') continue;
          if (typeof v === 'string' && v.length > MAX_PARAM_LEN) {
            v = v.slice(0, MAX_PARAM_LEN);
          }
          payload[k] = v;
        }
      }
      dl.push(payload);
    } catch (e) { /* never break the page for analytics */ }
  }

  /* Params attached to every event. */
  function base(extra) {
    var o = {
      surface: s.surface,
      product_handle: s.productHandle,
      case_size: SIZE_BY_UUID[String(s.modelUuid).slice(0, 8)] || '',
      model_uuid: s.modelUuid,
      variant_id: s.variantId
    };
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) o[k] = extra[k];
      }
    }
    return o;
  }

  /* The clock a given surface measures "time to X" from: modal open for B,
     page boot for A (where the viewer is present from load). */
  function originTs() {
    return s.openedAt || s.srcSetAt || s.bootAt;
  }

  function uuidFromSrc(src) {
    var m = /[?&]id=([^&#]+)/.exec(String(src || ''));
    return m ? m[1] : '';
  }

  /* ── First-interaction inference ───────────────────────────────────────
     A cross-origin iframe swallows its own pointer events, but focusing it
     blurs the parent window. window blur + activeElement === the Ikarus iframe
     is the standard proxy for "the shopper clicked/tapped into the model".
     Misses hover-only and pure-scroll interaction, so this is DIRECTIONAL —
     reported as roam_3d_interact and never treated as exact.
     Left installed for page life (one passive listener); re-armed per modal
     open by resetSession(). */
  function onWindowBlur() {
    // activeElement only updates after the blur event completes.
    setTimeout(function () {
      try {
        if (s.didInteract || !s.iframe) return;
        if (document.activeElement === s.iframe) markInteract();
      } catch (e) {}
    }, 0);
  }

  function markInteract() {
    if (s.didInteract) return;
    s.didInteract = true;
    track('roam_3d_interact', base({
      time_to_interact_ms: now() - originTs(),
      inferred: true
    }));
  }

  /* Any real input on the page. Gates recolor events so programmatic
     IKARUS_CONFIG sends during boot can never be mistaken for a user choice. */
  function markUserInput() {
    s.userInput = true;
  }

  /* ── Public API ────────────────────────────────────────────────────────── */
  var api = {
    /* Called once per surface, as early as possible. */
    init: function (opts) {
      try {
        opts = opts || {};
        s.surface = opts.surface || '';
        s.productHandle = opts.productHandle || '';
        if (opts.variantId) s.variantId = String(opts.variantId);
        if (opts.modelUuid) s.modelUuid = opts.modelUuid;

        ['pointerdown', 'keydown', 'touchstart'].forEach(function (evt) {
          document.addEventListener(evt, markUserInput, { passive: true, capture: true });
        });
        window.addEventListener('blur', onWindowBlur);
      } catch (e) {}
    },

    /* Register the iframe + the moment its src was set (start of load timing).
       Safe to call more than once; only the first src-set stamp is kept. */
    iframeReady: function (iframe) {
      try {
        if (!iframe) return;
        s.iframe = iframe;
        if (!s.modelUuid) s.modelUuid = uuidFromSrc(iframe.getAttribute('src') || iframe.src);
        if (!s.srcSetAt) {
          s.srcSetAt = now();
          if (s.watchdog) clearTimeout(s.watchdog);
          s.watchdog = setTimeout(function () {
            if (s.readyFired) return;
            // A backgrounded tab legitimately stalls the viewer (and browsers
            // throttle it), so that is not a failure worth reporting.
            try {
              if (document.visibilityState === 'hidden') return;
            } catch (e) {}
            api.error('ready_timeout');
          }, READY_TIMEOUT_MS);
        }
      } catch (e) {}
    },

    setVariantId: function (id) {
      try { if (id) s.variantId = String(id); } catch (e) {}
    },

    /* Viewer became visible: modal opened (B) or preview scrolled into view (A). */
    view: function (extra) {
      try {
        if (s.viewFired) return;
        s.viewFired = true;
        track('roam_3d_view', base(extra));
      } catch (e) {}
    },

    /* IKARUS_VIEWER_READY received. NOTE: this means the iframe booted and the
       viewer's JS is running — it is NOT proof the model finished painting.
       Ikarus has no render-complete message today; when they add one, fire a
       separate roam_3d_model_loaded rather than redefining this. */
    ready: function () {
      try {
        if (s.readyFired) return;
        s.readyFired = true;
        if (s.watchdog) { clearTimeout(s.watchdog); s.watchdog = null; }
        if (!s.modelUuid && s.iframe) s.modelUuid = uuidFromSrc(s.iframe.src);
        track('roam_3d_ready', base({
          load_ms: s.srcSetAt ? (now() - s.srcSetAt) : undefined
        }));
      } catch (e) {}
    },

    /* Called on every IKARUS_CONFIG we send. Filters aggressively — see the
       header notes. Only a genuine, user-driven, changed config gets through. */
    recolor: function (cfg) {
      try {
        if (!cfg) return;
        var keys = [];
        for (var k in cfg) {
          if (Object.prototype.hasOwnProperty.call(cfg, k) && cfg[k]) keys.push(k);
        }
        if (!keys.length) return;              // empty config — nothing to compare
        keys.sort();

        var hash = keys.map(function (key) { return key + ':' + cfg[key]; }).join('|');
        if (hash === s.lastHash) return;       // re-send of the same config

        var prev = s.lastConfig;
        s.lastHash = hash;
        s.lastConfig = cfg;

        if (!prev) return;                     // first non-empty config = baseline
        if (!s.userInput) return;              // programmatic (setDefaults/boot)

        // Accumulate the diff across the debounce window so a trim cascade
        // collapses into one event.
        if (!s.pendingFrom) s.pendingFrom = prev;
        if (s.recolorTimer) clearTimeout(s.recolorTimer);
        s.recolorTimer = setTimeout(function () {
          try {
            var from = s.pendingFrom || {};
            var to = s.lastConfig || {};
            s.pendingFrom = null;
            s.recolorTimer = null;

            var changed = [];
            for (var key in to) {
              if (!Object.prototype.hasOwnProperty.call(to, key)) continue;
              if (to[key] && to[key] !== from[key]) changed.push(key);
            }
            if (!changed.length) return;
            changed.sort();

            s.recolorCount++;
            track('roam_3d_recolor', base({
              component: changed.join('+'),
              color: to[changed[0]],
              recolor_index: s.recolorCount
            }));
          } catch (e) {}
        }, RECOLOR_DEBOUNCE_MS);
      } catch (e) {}
    },

    /* Customizer only: the size dropdown / Standard-Expandable toggle is an
       <a href> full page navigation. */
    sizeSwitch: function (toSize) {
      try {
        // No destination handle resolved → nothing worth recording.
        if (!toSize) return;
        track('roam_3d_size_switch', base({
          from_size: SIZE_BY_UUID[String(s.modelUuid).slice(0, 8)] || '',
          to_size: toSize
        }));
      } catch (e) {}
    },

    /* Modal only. */
    close: function () {
      try {
        track('roam_3d_close', base({
          dwell_ms: s.openedAt ? (now() - s.openedAt) : undefined,
          recolor_count: s.recolorCount,
          did_interact: s.didInteract ? 'true' : 'false'
        }));
      } catch (e) {}
    },

    /* Modal only: re-arm per-open counters so a second open is measured cleanly. */
    resetSession: function () {
      try {
        s.openedAt = now();
        s.viewFired = false;
        s.didInteract = false;
        s.recolorCount = 0;
      } catch (e) {}
    },

    error: function (reason, extra) {
      try {
        track('roam_3d_error', base({ error_reason: reason || 'unknown', detail: extra }));
      } catch (e) {}
    }
  };

  window.Roam3D = api;
})();
